export type ID = number | string;

/* PRODUCTS */

export interface Category {
  id: ID;
  image_url: null | string;
  products: ID[];
  title: string;
  icon: string;
  description: string;
  banner_text: string;
  on_offer: boolean;
  children: Category[];
}

export interface Product {
  id: ID;
  image_url: string;
  title: string;
  description: string;
  full_description: string;
  price: number;
  previous_price: number;
  banner_text: string;
  on_offer: boolean;
}

/* BASKET */

export interface Basket {
  id: number;
  description: null;
  latest_status: string;
  collection: null;
  delivery: null;
  items: BasketProductItem[];
  can_add_item: boolean;
  can_set_collection: boolean;
  can_set_delivery: boolean;
  price_descriptor: OrderPriceDescriptor;
}

export interface BasketProductItem {
  id: ID;
  quantity: number;
  product_title: string;
  product_id: ID;
  price: number;
  total_price: number;
}

export interface CategoryFAQ {
  question: string;
  answer: string;
}
/* PROFILE */

export interface Address {
  id: ID;
  title: string;
  address: string;
  latitude: number;
  longitude: number;
}

export interface Profile {
  id: ID;
  first_name: string;
  last_name: string;
  cellphone: string;
  email: string;
  balance: number;
}

/* TRANSPORTATION */

export interface TimeSheet {
  time_sheets: TimeSheetItem[];
  date: Date;
}

export interface TimeSheetItem {
  id: number;
  start: string;
  end: string;
  remain_capacity: number;
  disabled: boolean;
  filled: boolean;
}

/* ORDERS */
export interface Order {
  id: ID;
  description: null | string;
  latest_status: OrderLatestStatus;
  collection: OrderCollectionAndDelivery | null;
  delivery: OrderCollectionAndDelivery | null;
  price_descriptor: OrderPriceDescriptor;
  can_add_item: boolean;
  can_set_collection: boolean;
  can_set_delivery: boolean;
  can_add_coupon: boolean;
  can_remove_coupon: boolean;
  balance: number;
  gift_balance: number;
}

export interface OrderDetailed extends Order {
  items: BasketProductItem[];
}

export interface OrderDetailType {
  id: string;
  in_person: boolean;
  display_id: string;
  items: OrderDetailItemsType[];
  legacyInvoice: LegacyInvoiceType;
  legacyLogistics: LegacyLogisticsType;
  legacyPromotion: LegacyPromotionType;
  legacyHistoryLogistics: LegacyHistoryLogisticsType[];
  metadata: MetadataType;
  services: ListServicesType[];
}

export interface OrderDetailItemsType {
  coefficient: number | null;
  id: string;
  price: number;
  service: string;
  variation: string;
}
export interface LegacyInvoiceType {
  cash_back: number;
  discount: number;
  logistic_price: number;
  tax: number;
  toll: number;
  total_amount: number;
  total_items_amount: number;
  total_extra_charge: number;
  total_price: number;
  vat: number;
}
export interface LegacyLogisticsType {
  collection: {
    address_latitude: number;
    address_longitude: number;
    address_text: string;
    id: number;
    period: {
      date: string;
      end: string;
      id: number;
      start: string;
    };
    status: string;
  };
  delivery: {
    address_latitude: number;
    address_longitude: number;
    address_text: string;
    id: number;
    period: {
      date: string;
      end: string;
      id: number;
      start: string;
    };
    status: string;
  };
}
export interface LegacyPromotionType {
  code: string;
  description: string;
}

enum TaskTypeEnum {
  collection = "collection",
  delivery = "delivery",
}

enum TaskStatusEnum {
  unassigned = "unassigned",
  assigned = "assigned",
  loaded = "loaded",
  "not-present" = "not-present",
  done = "done",
  canceled = "canceled",
}
export interface LegacyHistoryLogisticsType {
  id: number;
  task_type: TaskTypeEnum;
  status: TaskStatusEnum;
  address_text: string;
  address_latitude: number;
  address_longitude: number;
  address_id?: number;
  period: {
    date: Date;
    end: string;
    id: number;
    start: string;
    title: string;
  };
  driver?: {
    id: number;
    first_name: string;
    last_name: string;
    cellphone: string;
  };
}
export interface MetadataType {
  legacy_factor_link: string;
  legacy_latest_status: string;
  can_cancel: boolean;
}

export interface OrderCollectionAndDelivery {
  id: ID;
  period: OrderPeriod;
  status: OrderStatus;
  address_text: string;
  address_latitude: number;
  address_longitude: number;
}

export interface OrderPeriod {
  id: ID;
  date: string;
  start: string;
  end: string;
}

export enum OrderStatus {
  Done = "done",
  Unassigned = "unassigned",
}

export enum OrderLatestStatus {
  Collected = "collected",
  ReadyToCollect = "ready_to_collect",
  SubmittedByCustomer = "submitted_by_customer",
  SubmittedByCRM = "submitted_by_crm",
  WorkDone = "work_done",
  CollectionCanceled = "collection_canceled",
  CollectionNotPresent = "collection_not_present",
  Unwrapped = "unwrapped",
  InProgress = "in_progress",
  ReadyToDeliver = "ready_to_deliver",
  DeliveryNotPresent = "delivery_not_present",
  DeliveryCanceled = "delivery_canceled",
  Delivered = "delivered",
  CanceledByCustomer = "canceled_by_customer",
  CanceledBySystem = "canceled_by_system",
}

export interface OrderPriceDescriptor {
  logistic_price: number;
  discount: number;
  total_items_amount: number;
  total_amount: number;
  total_price: number;
  free_items: FreeItem[];
  vat: number;
  tax: number;
  toll: number;
  cash_back: number;
  total_extra_charge: number;
}

export interface FreeItem {
  id: number;
  quantity: number;
  discount: number;
}

export interface AddCreditResponse {
  pay_id: string;
  psp_payment_url: string;
}

export interface SectionDataType {
  title: string;
  icon: string;
  action?: string;
  iconColor?: string;
  textColor?: string;
  hide?: boolean;
}

export interface TransactionsType {
  amount: number;
  description: string;
  id: number;
  is_gift: boolean;
  transaction_type: string;
}

export interface CategoriesType {
  data: {
    listCategories: ListCategoriesType[];
  };
}

export interface ListCategoriesType {
  id: string;
  alias: string;
  name: string;
  display_name: string;
}

export interface ServicesType {
  data: {
    listServices: ListServicesType[];
  };
}
export interface PopularServicesType {
  data: {
    listPopularServices: ListServicesType[];
  };
}

export interface SelectedServiceType {
  id: string;
  alias: string;
  name: string;
  display_name?: string;
  description?: string;
  pricing: Pricing[];
  children: SelectedServiceType[];
}
export interface ServiceVariation {
  id: string;
  alias: string;
  name: string;
  unit: string;
}

interface Pricing {
  id: string;
  price: number;
  variation: ServiceVariation;
}

export interface ListServicesType {
  id: string;
  alias: string;
  name: string;
  display_name?: string;
  description?: string;
  pricing: Pricing[];
  children: ListServicesType[];
}

export type TransportationType = "collection" | "delivery";

export interface LogisticScheduledTaskPeriod {
  id: number;
  date: Date;
  start: string;
  end: string;
}

export interface LogisticScheduledTaskAddress {
  id: number;
  address_text: string;
}

export interface LogisticScheduledTask {
  period: LogisticScheduledTaskPeriod;
  address: LogisticScheduledTaskAddress;
}

export interface LegacyHistoryLogisticsType {
  id: number;
  task_type: TaskTypeEnum;
  status: TaskStatusEnum;
  address_text: string;
  address_latitude: number;
  address_longitude: number;
  address_id?: number;
  period: {
    date: Date;
    end: string;
    id: number;
    start: string;
    title: string;
  };
  driver?: {
    id: number;
    first_name: string;
    last_name: string;
    cellphone: string;
  };
}

export interface HomePageContentType {
  promotions: HomePagePromotionsType[];
  videos: HomePageVideosType[];
  promotionNewUsers: HomePagePromotionNewUsersType[];
  appInfo: HomePageAppInfoType[];
}

export interface HomePagePromotionsType {
  code: string;
  description: string;
  ratio: string;
  title: string;
  url: string;
}

export interface HomePageVideosType {
  cover: string;
  id: string;
  ratio: string;
  url: string;
}

export interface HomePagePromotionNewUsersType {
  code: string;
  title: string;
}

export interface HomePageAppInfoType {
  min_version: string;
  max_version: string;
  required_version: string;
  phone: string;
  text: string;
}
